import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { alertMessage } from "app/redux/actions/common";
import { getClientsAPI } from "app/services/clients";
import {
  getNotificationCampaignByIdApi,
  getNotificationCampaignRunsApi,
  getNotificationCampaignsApi,
  postNotificationCampaignApi,
  postNotificationCampaignTestApi,
  putNotificationCampaignApi,
} from "app/services/notificationCampaigns";
import { errorHandler } from "app/shared/Service/errorHandler";
import { PaginationInterfaceLF } from "app/types";
import { Client } from "app/types/clients";
import {
  NotificationCampaign,
  NotificationCampaignFilters,
  NotificationCampaignRun,
  TestNotificationCampaignPayload,
  UpsertNotificationCampaignPayload,
} from "app/types/notification-campaigns";
import { createQuery, getActiveConfig } from "app/utils/constants/common";
import { AppDispatch } from "../store";
import { RootState } from "../reducers/hooks";

export const NOTIFICATION_CAMPAIGN_CLIENTS_OPTIONS_LIMIT = 50;

interface NotificationCampaignState {
  list: NotificationCampaign[];
  pagination: PaginationInterfaceLF | null;
  listRowPerPage: number;
  filters: NotificationCampaignFilters | null;
  isListLoading: boolean;
  isDetailsLoading: boolean;
  isSubmitting: boolean;
  runsList: NotificationCampaignRun[];
  isRunsLoading: boolean;
  clientsOptionsList: Client[];
  isClientsOptionsLoading: boolean;
  isTestSubmitting: boolean;
}

const initialState: NotificationCampaignState = {
  list: [],
  pagination: null,
  listRowPerPage: 50,
  filters: null,
  isListLoading: false,
  isDetailsLoading: false,
  isSubmitting: false,
  runsList: [],
  isRunsLoading: false,
  clientsOptionsList: [],
  isClientsOptionsLoading: false,
  isTestSubmitting: false,
};

export const notificationCampaignSlice = createSlice({
  name: "notificationCampaign",
  initialState,
  reducers: {
    setList: (
      state,
      action: PayloadAction<{
        list: NotificationCampaign[];
        pagination: PaginationInterfaceLF;
      }>,
    ) => {
      state.list = action.payload.list;
      state.pagination = action.payload.pagination;
    },
    setListLoading: (state, action: PayloadAction<boolean>) => {
      state.isListLoading = action.payload;
    },
    setListRowPerPage: (state, action: PayloadAction<number>) => {
      state.listRowPerPage = action.payload;
    },
    setFilters: (
      state,
      action: PayloadAction<NotificationCampaignFilters | null>,
    ) => {
      state.filters = action.payload;
    },
    setDetailsLoading: (state, action: PayloadAction<boolean>) => {
      state.isDetailsLoading = action.payload;
    },
    setSubmitting: (state, action: PayloadAction<boolean>) => {
      state.isSubmitting = action.payload;
    },
    setRunsList: (state, action: PayloadAction<NotificationCampaignRun[]>) => {
      state.runsList = action.payload;
    },
    setRunsLoading: (state, action: PayloadAction<boolean>) => {
      state.isRunsLoading = action.payload;
    },
    clearRunsList: (state) => {
      state.runsList = [];
    },
    setClientsOptionsList: (state, action: PayloadAction<Client[]>) => {
      state.clientsOptionsList = action.payload;
    },
    setClientsOptionsLoading: (state, action: PayloadAction<boolean>) => {
      state.isClientsOptionsLoading = action.payload;
    },
    setTestSubmitting: (state, action: PayloadAction<boolean>) => {
      state.isTestSubmitting = action.payload;
    },
  },
});

export const {
  setList,
  setListLoading,
  setListRowPerPage,
  setFilters,
  setDetailsLoading,
  setSubmitting,
  setRunsList,
  setRunsLoading,
  clearRunsList,
  setClientsOptionsList,
  setClientsOptionsLoading,
  setTestSubmitting,
} = notificationCampaignSlice.actions;

export default notificationCampaignSlice.reducer;

export const getNotificationCampaignsListAction = (
  { continutationToken }: { continutationToken?: string | null } = {
    continutationToken: null,
  },
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const { storeId } = getActiveConfig();
      const { filters, listRowPerPage } = getState().notificationCampaign;
      const query =
        "?" +
        createQuery({
          filter: {
            ...(filters || {}),
            limit: listRowPerPage,
            continutationToken,
          },
        });

      dispatch(setListLoading(true));
      const { data } = await getNotificationCampaignsApi(storeId, query);
      dispatch(setListLoading(false));
      dispatch(
        setList({
          list: data.data,
          pagination: data.pagination,
        }),
      );
    } catch (error) {
      dispatch(setListLoading(false));
      dispatch(alertMessage(errorHandler(error), "error"));
    }
  };
};

export const getNotificationCampaignByIdAction = (
  storeId: number,
  campaignId: number,
  callback?: (data: NotificationCampaign | null) => void,
) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setDetailsLoading(true));
      const { data } = await getNotificationCampaignByIdApi(storeId, campaignId);
      dispatch(setDetailsLoading(false));

      if (data?.data) {
        if (callback) callback(data.data);
        return data.data;
      }

      if (callback) callback(null);
      return null;
    } catch (error) {
      dispatch(setDetailsLoading(false));
      dispatch(alertMessage(errorHandler(error), "error"));
      if (callback) callback(null);
    }
  };
};

type PostNotificationCampaignActionParams = {
  storeId: number;
  payload: UpsertNotificationCampaignPayload;
};

export const postNotificationCampaignAction =
  ({ storeId, payload }: PostNotificationCampaignActionParams) =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(setSubmitting(true));
      const { data } = await postNotificationCampaignApi({ storeId, payload });
      dispatch(setSubmitting(false));

      if (data?.data) {
        dispatch(alertMessage("Campaign created successfully", "success"));
        dispatch(getNotificationCampaignsListAction({ continutationToken: null }));
        return data.data;
      }
    } catch (error) {
      dispatch(setSubmitting(false));
      dispatch(alertMessage(errorHandler(error), "error"));
    }
  };

type PutNotificationCampaignActionParams = {
  storeId: number;
  campaignId: number;
  payload: UpsertNotificationCampaignPayload;
};

export const putNotificationCampaignAction =
  ({ storeId, campaignId, payload }: PutNotificationCampaignActionParams) =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(setSubmitting(true));
      const { data } = await putNotificationCampaignApi({
        storeId,
        campaignId,
        payload,
      });
      dispatch(setSubmitting(false));

      if (data?.data) {
        dispatch(alertMessage("Campaign updated successfully", "success"));
        dispatch(getNotificationCampaignsListAction({ continutationToken: null }));
        return data.data;
      }
    } catch (error) {
      dispatch(setSubmitting(false));
      dispatch(alertMessage(errorHandler(error), "error"));
    }
  };

export const getNotificationCampaignRunsAction = (campaignId: number) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      dispatch(setRunsLoading(true));
      const { data } = await getNotificationCampaignRunsApi(storeId, campaignId);
      dispatch(setRunsLoading(false));
      dispatch(setRunsList(data.data || []));
    } catch (error) {
      dispatch(setRunsLoading(false));
      dispatch(alertMessage(errorHandler(error), "error"));
    }
  };
};

export const getNotificationCampaignClientsAction = ({
  search = "",
}: { search?: string } = {}) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { brand } = getActiveConfig();

      dispatch(setClientsOptionsLoading(true));
      const query =
        "?" +
        createQuery({
          filter: {
            limit: NOTIFICATION_CAMPAIGN_CLIENTS_OPTIONS_LIMIT,
            brandId: brand.brandId,
            ...(search ? { search } : {}),
          },
        });
      const { data } = await getClientsAPI(query);
      const clients = data.data || [];

      dispatch(setClientsOptionsList(clients));
      dispatch(setClientsOptionsLoading(false));
      return clients;
    } catch (error) {
      dispatch(setClientsOptionsLoading(false));
      dispatch(alertMessage(errorHandler(error), "error"));
    }
  };
};

type TestNotificationCampaignActionParams = {
  storeId: number;
  payload: TestNotificationCampaignPayload;
};

export const testNotificationCampaignAction = ({
  storeId,
  payload,
}: TestNotificationCampaignActionParams) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setTestSubmitting(true));
      const { data } = await postNotificationCampaignTestApi({
        storeId,
        payload,
      });
      dispatch(setTestSubmitting(false));

      if (data?.data) {
        dispatch(
          alertMessage(
            `Test sent to ${data.data.totalSent} of ${data.data.totalRecipients} recipients`,
            "success",
          ),
        );
        return data.data;
      }
    } catch (error) {
      dispatch(setTestSubmitting(false));
      dispatch(alertMessage(errorHandler(error), "error"));
    }
  };
};
